// utils/conversationLogger.js
// Automatic conversation tracking for bugs, enhancements, and version releases
// All state persists to Firestore for cross-session compatibility
import { createLogEntry, updateLogEntry, getRecentUnresolvedIssues, getLogEntry, addLogNote } from "./adminLogs";
import { createVersionEntry, incrementVersion, getAllVersions } from "./releaseVersions";
import { APP_VERSION } from "../version";

/**
 * Track a bug or enhancement mentioned in conversation
 * Automatically creates a log entry in Firestore
 * 
 * @param {Object} issueData
 * @param {string} issueData.type - "bug" or "enhancement"
 * @param {string} issueData.title - Brief title of the issue
 * @param {string} issueData.description - Detailed description
 * @param {string} issueData.priority - "high", "medium", or "low" (default: "medium")
 * @param {string[]} issueData.tags - Array of tags for categorization
 * @param {string} issueData.conversationContext - Optional context about when/why this was mentioned
 * @param {string} issueData.conversationSessionId - Optional session ID for grouping
 * @returns {Promise<string>} - The created entry ID
 */
export async function trackConversationIssue({
  type,
  title,
  description = "",
  priority = "medium",
  tags = [],
  conversationContext = "",
  conversationSessionId = null,
}) {
  try {
    // Enhance description with context if provided
    const fullDescription = conversationContext
      ? `${description}\n\n[Conversation Context] ${conversationContext}`
      : description;

    const entry = await createLogEntry({
      type,
      title,
      description: fullDescription,
      priority,
      tags,
      createdBy: "ai-assistant",
      conversationSessionId,
    });

    console.log(`✅ Auto-logged ${type}: ${entry.id} - ${title}`);
    return entry.id;
  } catch (err) {
    console.error("Error tracking conversation issue:", err);
    throw err;
  }
}

/**
 * Mark an issue as resolved and link it to a version
 * 
 * @param {string} entryId - The log entry ID to mark as resolved
 * @param {string} versionId - The version ID (e.g., "v001") this was resolved in
 * @param {string} notes - Optional notes about the resolution
 */
export async function markIssueResolved(entryId, versionId, notes = "") {
  try {
    const entry = await getLogEntry(entryId);
    if (!entry) {
      throw new Error(`Entry ${entryId} not found`);
    }

    // Determine new status based on type
    const newStatus = entry.type === "bug" ? "resolved" : "completed";

    await updateLogEntry(entryId, {
      status: newStatus,
      resolvedInVersion: versionId,
    });

    if (notes) {
      await addLogNote(entryId, notes, `Status changed to ${newStatus}`);
    }

    console.log(`✅ Marked ${entryId} as ${newStatus} in ${versionId}`);
  } catch (err) {
    console.error("Error marking issue as resolved:", err);
    throw err;
  }
}

/**
 * Create a version entry from accepted code changes
 * Automatically links resolved issues and increments version
 * 
 * @param {Object} options
 * @param {string[]} options.resolvedIssueIds - Array of log entry IDs that were resolved
 * @param {Array} options.changes - Array of change objects: { type, description, logEntryId? }
 * @param {string} options.notes - Optional release notes
 * @param {string} options.version - Optional version string (auto-increments if not provided)
 * @param {string} options.incrementType - "patch", "minor", or "major" (default: "patch")
 * @returns {Promise<Object>} - The created version entry
 */
export async function createConversationVersion({
  resolvedIssueIds = [],
  changes = [],
  notes = "",
  version = null,
  incrementType = "patch",
}) {
  try {
    // Get current version if not provided
    let newVersion = version;
    if (!newVersion) {
      // Get latest version from Firestore or use APP_VERSION
      const allVersions = await getAllVersions();
      const latestVersion = allVersions.length > 0 
        ? allVersions[0].version 
        : APP_VERSION;
      
      newVersion = incrementVersion(latestVersion, incrementType);
    }

    // Build changes array from resolved issues
    const versionChanges = [];
    
    // First, add changes from resolved issues
    for (const issueId of resolvedIssueIds) {
      const issue = await getLogEntry(issueId);
      if (issue) {
        versionChanges.push({
          type: issue.type,
          description: `${issue.title}: ${issue.description || ""}`,
          logEntryId: issueId,
        });
      }
    }
    
    // Then add any additional manual changes
    versionChanges.push(...changes);

    // Create version entry
    const versionEntry = await createVersionEntry({
      version: newVersion,
      releaseDate: new Date(),
      changes: versionChanges,
      notes: notes || `Auto-generated version entry with ${resolvedIssueIds.length} resolved issues`,
      createdBy: "ai-assistant",
    });

    // Mark all resolved issues as resolved/completed
    for (const issueId of resolvedIssueIds) {
      await markIssueResolved(issueId, versionEntry.id, `Resolved in ${versionEntry.id} (${newVersion})`);
    }

    console.log(`✅ Created version ${newVersion} (${versionEntry.id}) with ${resolvedIssueIds.length} resolved issues`);
    return versionEntry;
  } catch (err) {
    console.error("Error creating conversation version:", err);
    throw err;
  }
}

/**
 * Get recent unresolved issues (helper for finding what's been worked on)
 * This works across agent sessions by querying Firestore
 * 
 * @param {number} hours - Hours to look back (default: 24)
 * @returns {Promise<Array>} - Array of unresolved log entries
 */
export async function getRecentActiveIssues(hours = 24) {
  try {
    return await getRecentUnresolvedIssues(hours);
  } catch (err) {
    console.error("Error getting recent active issues:", err);
    throw err;
  }
}

/**
 * Helper to automatically detect and log bugs/enhancements from conversation
 * This is a convenience wrapper that extracts info from user messages
 * 
 * @param {string} userMessage - The user's message
 * @param {Object} options - Additional options
 * @returns {Promise<string|null>} - Entry ID if created, null otherwise
 */
export async function autoDetectAndLogIssue(userMessage, options = {}) {
  const message = userMessage.toLowerCase().trim();
  
  // Detect bug mentions
  const bugPatterns = [
    /(?:bug|error|crash|broken|issue|problem|fails?|doesn'?t work|not working)/i,
  ];
  
  // Detect enhancement/feature mentions
  const enhancementPatterns = [
    /(?:feature|enhancement|improvement|add|can we|should we|would be nice)/i,
  ];
  
  let type = null;
  let priority = options.priority || "medium";
  
  // Check for high priority indicators
  if (/(?:critical|urgent|major|severe|breaking)/i.test(message)) {
    priority = "high";
  }
  
  // Determine type
  if (bugPatterns.some(p => p.test(message))) {
    type = "bug";
  } else if (enhancementPatterns.some(p => p.test(message))) {
    type = "enhancement";
  }
  
  if (!type) {
    return null; // No issue detected
  }
  
  // Extract title (first sentence or first 100 chars)
  const titleMatch = userMessage.match(/^[^.!?]+[.!?]?/);
  let title = titleMatch ? titleMatch[0].trim() : userMessage.substring(0, 100).trim();
  if (title.length > 100) {
    title = title.substring(0, 97) + "...";
  }
  
  // Use full message as description
  const description = userMessage;
  
  return await trackConversationIssue({
    type,
    title,
    description,
    priority,
    tags: options.tags || [],
    conversationContext: options.context || "Auto-detected from conversation",
    conversationSessionId: options.sessionId || null,
  });
}
